/**
 * Copyright (c) 2018 Honeywell International Inc. All Rights Reserved.
 * Honeywell and its product names are among the trademarks and/or service marks owned by Honeywell International, Inc., or its subsidiaries.
 *
 * For patent information, see http://www.hsmpats.com.
 */

import React from 'react';
import APIClient from '../../common/APIClient';
import { withApplicationContext, withCustomerSiteContext } from '../../common/HOCs';

export const OperatorContext = React.createContext();
export const OperatorConsumer = OperatorContext.Consumer;

class OperatorProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      operators: null,
      templates: null,
      selectedRows: [],
      selectedTemplateRow: [],
      uploadedFile: null,
      isConfirmDeleteModalOpen: false,
      isConfirmTemplateDeleteModalOpen: false,
      isConfirmTemplateDeleteAllModalOpen: false,
      selectedCustomer: '',
      selectedOperator: '',
      opSettings: null,
    };
  }

  render() {
    return (
      <OperatorContext.Provider
        value={{
          // state vars
          operators: this.state.operators,
          templates: this.state.templates,
          selectedRows: this.state.selectedRows,
          selectedTemplateRow: this.state.selectedTemplateRow,
          uploadedFile: this.state.uploadedFile,
          isConfirmDeleteModalOpen: this.state.isConfirmDeleteModalOpen,
          isConfirmSingleDeleteModalOpen: this.state.isConfirmSingleDeleteModalOpen,
          isConfirmTemplateDeleteModalOpen: this.state.isConfirmTemplateDeleteModalOpen,
          isConfirmTemplateDeleteAllModalOpen: this.state.isConfirmTemplateDeleteAllModalOpen,
          selectedOperator: this.state.selectedOperator,
          opSettings: this.state.opSettings,

          // api methods
          createOperator: this.createOperator,
          editOperator: this.editOperator,
          fetchOperators: this.fetchOperators,
          fetchTemplates: this.fetchTemplates,
          deleteTemplate: this.deleteTemplate,
          deleteAllTemplate: this.deleteAllTemplate,
          uploadTemplates: this.uploadTemplates,
          deleteOperators: this.deleteOperators,
          deleteOperator: this.deleteOperator,
          toggleConfirmDeleteOperatorsModal: this.toggleConfirmDeleteOperatorsModal,
          toggleConfirmSingleDeleteOperatorModal: this.toggleConfirmSingleDeleteOperatorModal,
          toggleConfirmTemplateDeleteOperatorModal: this.toggleConfirmTemplateDeleteOperatorModal,
          toggleConfirmTemplateDeleteAllOperatorModal: this
            .toggleConfirmTemplateDeleteAllOperatorModal,
          setSelectedRows: this.setSelectedRows,
          setTemplateSelectedRow: this.setTemplateSelectedRow,
          getIdToken: this.getIdToken,
          clearSelectedOperators: this.clearSelectedOperators,
          setSelectedOperator: this.setSelectedOperator,
          clearSelectedOperatorTemplates: this.clearSelectedOperatorTemplates,
          fetchOperatorSettings: this.fetchOperatorSettings,
        }}
      >
        {this.props.children}
      </OperatorContext.Provider>
    );
  }

  createOperator = async (createOperatorForm) => {
    try {
      const response = await APIClient.createOperator(
        createOperatorForm,
        this.props.appContext.user.idToken,
        this.props.custSiteContext.selectedCustomer
      );
      this.props.appContext.setNotification(
        `Operator ${createOperatorForm.operatorId} was created.`,
        'success'
      );
    } catch (err) {
      return this.props.appContext.handleModalError(err);
    }
  };

  fetchOperators = async (signal) => {
    let operators = [];
    try {
      // EVS2-822 : If the value of siteid is null then make it -1, which s the default site.
      let selectedSiteId = -1;
      if (
        this.props.custSiteContext.selectedSite != undefined &&
        this.props.custSiteContext.selectedSite != null &&
        this.props.custSiteContext.selectedSite != ''
      )
        selectedSiteId = this.props.custSiteContext.selectedSite;

      operators = await APIClient.fetchOperators(
        this.props.appContext.user.idToken,
        this.props.custSiteContext.selectedCustomer,
        selectedSiteId,
        signal
      );
      operators = operators.response;
      this.setState({ operators });
      return operators;
    } catch (err) {
      this.setState({ operators });
      if (err.name !== 'AbortError') {
        this.props.appContext.handleError(err);
        console.error(err);
      }
      return operators;
    }
  };

  fetchTemplates = async (operator, signal) => {
    try {
      const templates = await APIClient.fetchOperatorTemplates(
        operator,
        this.props.appContext.user.idToken,
        this.props.custSiteContext.selectedCustomer,
        signal
      );
      this.setState({ templates: templates.LinkedList });
    } catch (err) {
      if (err.name !== 'AbortError') {
        return this.props.appContext.handleError(err);
        console.error(err);
      }
    }
  };

  deleteTemplate = async (operator) => {
    try {
      const response = await APIClient.deleteTemplate(
        operator,
        this.selectedTemplateRow,
        this.props.appContext.user.idToken,
        this.props.custSiteContext.selectedCustomer
      );
      this.props.appContext.setNotification(
        `Deleted \'${this.selectedTemplateRow.word}\' template.`,
        'success'
      );
    } catch (err) {
      return this.props.appContext.handleError(err);
    } finally {
      this.setState({ selectedTemplateRow: [] });
    }
  };

  deleteAllTemplate = async (operator) => {
    try {
      const response = await APIClient.deleteAllTemplate(
        operator,
        this.props.appContext.user.idToken,
        this.props.custSiteContext.selectedCustomer
      );
      this.props.appContext.setNotification(`Deleted all templates.`, 'success');
    } catch (err) {
      return this.props.appContext.handleError(err);
    } finally {
    }
  };

  uploadTemplates = async (data) => {
    try {
      const response = await APIClient.uploadTemplates(
        data,
        this.props.appContext.user.idToken,
        this.props.custSiteContext.selectedCustomer
      );
      this.props.appContext.setNotification(`Templates uploaded`, 'success');
    } catch (err) {
      return this.props.appContext.handleError(err);
    }
  };

  editOperator = async (editOperatorForm) => {
    try {
      const response = await APIClient.editOperator(
        editOperatorForm,
        this.props.appContext.user.idToken,
        this.props.custSiteContext.selectedCustomer
      );
      this.props.appContext.setNotification(
        `Operator ${editOperatorForm.operatorId} was modified.`,
        'success'
      );
      this.setSelectedRows([]);
    } catch (err) {
      return this.props.appContext.handleModalError(err);
    }
  };

  deleteOperators = async () => {
    try {
      let message = '';
      let operatorIds = '';
      let operCount = 0;
      const operatorArray = this.state.selectedRows;
      if (operatorArray.length > 1) {
        operatorArray.forEach(function (operator) {
          if (operCount > 0) {
            operatorIds = `${operatorIds};${operator.operatorId}`;
          } else {
            operatorIds = operator.operatorId;
          }
          operCount++;
        });
        message = 'Operaters deleted successfully.';
      } else {
        operatorIds = operatorArray[0].operatorId;
        message = 'Operater deleted successfully.';
      }
      const response = await APIClient.deleteOperators(
        operatorIds,
        this.props.appContext.user.idToken,
        this.props.custSiteContext.selectedCustomer
      );
      this.setState({
        isConfirmDeleteModalOpen: !this.state.isConfirmDeleteModalOpen,
        selectedRows: [],
      });
      this.props.appContext.setNotification(message, 'success');
    } catch (err) {
      this.setState({
        isConfirmDeleteModalOpen: !this.state.isConfirmDeleteModalOpen,
        selectedRows: [],
      });
      return this.props.appContext.handleError(err);
    }
  };

  deleteOperator = async () => {
    try {
      const message = 'Operator deleted successfully.';
      const response = await APIClient.deleteOperators(
        this.state.selectedOperator.operatorId,
        this.props.appContext.user.idToken,
        this.props.custSiteContext.selectedCustomer
      );
      this.setState({
        isConfirmSingleDeleteModalOpen: !this.state.isConfirmSingleDeleteModalOpen,
        selectedRows: [],
      });
      this.props.appContext.setNotification(message, 'success');
    } catch (err) {
      this.setState({
        isConfirmSingleDeleteModalOpen: !this.state.isConfirmSingleDeleteModalOpen,
        selectedRows: [],
      });
      return this.props.appContext.handleError(err);
    }
  };

  fetchOperatorSettings = async (operator) => {
    try {
      const opSettings = await APIClient.fetchOperatorSettings(
        operator,
        this.props.appContext.user.idToken,
        this.props.custSiteContext.selectedCustomer
      );
      return opSettings.response;
    } catch (err) {
      return this.props.appContext.handleModalError(err);
    }
  };

  toggleConfirmDeleteOperatorsModal = () => {
    this.setState({ isConfirmDeleteModalOpen: !this.state.isConfirmDeleteModalOpen });
  };

  toggleConfirmSingleDeleteOperatorModal = () => {
    this.setState({ isConfirmSingleDeleteModalOpen: !this.state.isConfirmSingleDeleteModalOpen });
  };

  clearSelectedOperators = () => {
    this.setState({ selectedRows: [] });
  };

  clearSelectedOperatorTemplates = () => {
    this.setState({ templates: [] });
  };

  toggleConfirmTemplateDeleteOperatorModal = () => {
    this.setState({
      isConfirmTemplateDeleteModalOpen: !this.state.isConfirmTemplateDeleteModalOpen,
    });
  };

  toggleConfirmTemplateDeleteAllOperatorModal = () => {
    this.setState({
      isConfirmTemplateDeleteAllModalOpen: !this.state.isConfirmTemplateDeleteAllModalOpen,
    });
  };

  setSelectedRows = (selectedRows) => {
    this.setState({ selectedRows });
  };

  setTemplateSelectedRow = (selectedTemplateRow) => {
    this.selectedTemplateRow = selectedTemplateRow;
  };

  getIdToken = () => {
    return this.props.appContext.user.idToken;
  };

  setSelectedOperator = async (operator) => {
    this.setState({ selectedOperator: operator });
  };
}

export default withApplicationContext(withCustomerSiteContext(OperatorProvider));
